import { computed, onScopeDispose, ref, watchEffect, type MaybeRefOrGetter, type Ref, toValue } from 'vue'

import { isHexColor, normalizeHex, onBrandContrast, onBrandFor } from './contrast'

/** Aké premenné smie white-label vrstva prepísať. Nič iné partner neovplyvňuje. */
export interface BrandTheme {
	/** Znormalizovaný brand hex — to, čo sa naozaj zapíše do `--color-brand`. */
	readonly brand: string
	/** Odvodené popredie — `--color-on-brand`. */
	readonly onBrand: string
	/** Kontrast dvojice; admin ho zobrazuje pri výbere farby. */
	readonly contrast: number
	/** `false`, keď partner zadal nezmysel a použil sa fallback. */
	readonly valid: boolean
}

export interface UseBrandThemeOptions {
	/**
	 * Kam sa premenné zapíšu. Predvolene `document.documentElement`, aby brand
	 * platil aj pre portály (dialóg, popover) mimo `#app`.
	 *
	 * Živý náhľad v admine si sem podstrčí vlastný element — vtedy sa prefarbí
	 * len karta náhľadu, nie celá aplikácia.
	 */
	target?: MaybeRefOrGetter<HTMLElement | null | undefined>
	/** Použije sa, keď je vstup neplatný. Predvolene predvolený brand importéra. */
	fallback?: string
}

/** Predvolený brand z `tokens.css`; drží sa tu, aby fallback nebol prázdny reťazec. */
const DEFAULT_BRAND = '#059669'

/**
 * White-label vrstva ako composable.
 *
 * Berie brand hex (z profilu partnera, z admin formulára alebo z náhľadu),
 * odvodí `--color-on-brand` kontrastným pravidlom a zapíše obe premenné do DOM.
 * Zvyšok aplikácie o brande nevie — používa iba `bg-brand` / `text-on-brand`,
 * takže prefarbenie je jedna operácia a nie prehľadávanie komponentov.
 *
 * Pri odchode zo scope sa premenné odstránia, aby náhľad v admine nepretiekol
 * do zvyšku aplikácie.
 */
export function useBrandTheme(brandHex: MaybeRefOrGetter<string | null | undefined>, options: UseBrandThemeOptions = {}): Ref<BrandTheme> {
	const fallback = normalizeHex(options.fallback ?? DEFAULT_BRAND)

	const theme = computed<BrandTheme>(() => {
		const raw = toValue(brandHex)
		const valid = typeof raw === 'string' && isHexColor(raw)
		const brand = valid ? normalizeHex(raw) : fallback

		return {
			brand,
			onBrand: onBrandFor(brand),
			contrast: onBrandContrast(brand),
			valid,
		}
	})

	const applied = ref<HTMLElement | null>(null)

	watchEffect(() => {
		const explicit = toValue(options.target)
		const element = explicit ?? (typeof document === 'undefined' ? null : document.documentElement)
		if (element === null || element === undefined) {
			return
		}

		// Keď sa cieľ zmenil, starý element sa musí vyčistiť — inak by v admine
		// zostala prefarbená karta po prepnutí na iného partnera.
		if (applied.value !== null && applied.value !== element) {
			clearBrandTheme(applied.value)
		}

		element.style.setProperty('--color-brand', theme.value.brand)
		element.style.setProperty('--color-on-brand', theme.value.onBrand)
		applied.value = element
	})

	onScopeDispose(() => {
		if (applied.value !== null) {
			clearBrandTheme(applied.value)
			applied.value = null
		}
	})

	return theme
}

/** Vráti element k hodnotám z `tokens.css`. */
export function clearBrandTheme(element: HTMLElement): void {
	element.style.removeProperty('--color-brand')
	element.style.removeProperty('--color-on-brand')
}
